<template>
  <div class="p-4">
    <div class="mb-4 w-70">
      <uiButton v-if="!hasFreeSub" @click="createFreeSubscription">
        Выдать бесплатную подписку
      </uiButton>
      <uiButton v-else color="red" @click="cancelFreeSubscription">
        Отменить бесплатную подписку
      </uiButton>
    </div>

    <template v-if="subscriptions.length">
      <div
        class="text-sm grid gap-y-3 grid-cols-[130px,200px,100px,120px,70px,220px,250px,1fr]"
      >
        <div class="opacity-50">Статус</div>
        <div class="opacity-50">Описание</div>
        <div class="opacity-50">Период</div>
        <div class="opacity-50">Стоимость</div>
        <div class="opacity-50">Триал</div>
        <div class="opacity-50">Причина</div>
        <div class="opacity-50">История</div>
        <div></div>

        <template v-for="sub in subscriptions">
          <div>
            <div class="flex space-x-2 items-center">
              <div
                class="rounded-full h-1.3 w-1.3"
                :class="[sub.active ? 'bg-green-500' : 'bg-red-500']"
              />
              <span>{{ sub.active ? 'Активная' : 'Неактивная' }}</span>
            </div>
          </div>
          <div>{{ sub.description }}</div>
          <div>
            {{ sub.period }}
            {{ pluralize(sub.period, ...intervalMap[sub.interval]) }}
          </div>
          <div>{{ sub.price }} {{ sub.currency }}</div>
          <div>{{ sub.trial ? 'Да' : '–' }}</div>
          <div>
            {{
              sub.endedReason
                ? endedReasonMap[sub.endedReason] ?? sub.endedReason
                : '–'
            }}
          </div>
          <div>
            <span v-for="event in sub.history" :key="event._id">
              {{
                event.status
                  ? historyStatusMap[event.status] ?? event.status
                  : '–'
              }}
              {{ formatDate(event.date) }}
            </span>
          </div>
          <div>
            <uiButton
              v-if="sub.active && sub.providerId !== 'free'"
              :loading="loading"
              color="red"
              class="w-40"
              @click="cancelSubscription(sub._id)"
            >
              Отменить
            </uiButton>
          </div>
        </template>
      </div>
    </template>

    <div v-else>
      <p class="m-0 text-base">Нет подписок</p>
    </div>
  </div>
</template>

<script>
import { API } from '@/config'
import { format, formatDistanceToNow } from 'date-fns'
import { ru } from 'date-fns/locale'
import { pluralize } from 'numeralize-ru'
import { useAsyncMethod } from '@/utils/async-methods'

const intervalMap = {
  day: ['день', 'дня', 'дней'],
  week: ['неделя', 'недели', 'недель'],
  month: ['месяц', 'месяца', 'месяцев'],
  year: ['год', 'года', 'лет']
}

const historyStatusMap = {
  created: 'Создана',
  payed: 'Оплачена',
  failed: 'Неудачна',
  rejected: 'Отклонена',
  ended: 'Завершилась',
  updateCard: 'Обновлена карта'
}

const endedReasonMap = {
  rejected: 'Отклонена',
  canceledUser: 'Отменена пользователем',
  canceledAdmin: 'Отменена админом',
  canceledMerchant: 'Отменена мерчантом'
}

export default {
  name: 'UserSubscriptions',
  props: {
    id: String
  },
  data () {
    return {
      loading: false,
      subscriptions: [],
      pluralize,
      intervalMap,
      historyStatusMap,
      endedReasonMap
    }
  },
  watch: {
    id () {
      this.subscriptions = []
      this.load()
    }
  },
  created () {
    this.load()
  },
  computed: {
    hasFreeSub () {
      // ended engine comps stay in the list as the accounting trail
      return this.subscriptions.some(sub => sub.providerId === 'free' && sub.active)
    }
  },
  methods: {
    async fetchSubscriptions () {
      const { data } = await this.$http.post(API.USERS.subscriptions, {
        id: this.id
      })
      if (!data || data.err) {
        this.$notify(data.err)
        return (this.loading = false)
      }

      this.subscriptions = data.sort((a, b) => b.active - a.active)
      this.$emit('loaded', data.length)
    },
    async load () {
      if (this.loading) return
      this.loading = true

      await this.fetchSubscriptions()

      this.loading = false
    },
    formatDate (d) {
      if (!d) return ''
      return format(new Date(d), 'dd.MM.yyyy в HH:mm')
    },
    async cancelSubscription (subId) {
      const confirm = await this.$confirm('Точно отменяем?')
      if (!confirm) return

      this.loading = true
      const { data } = await this.$http.post(API.USERS.cancelSubscription, {
        userId: this.id,
        subId
      })
      if (!data || data.err) {
        this.$notify(data.err)
        return (this.loading = false)
      }

      await this.fetchSubscriptions()
      this.loading = false
    },
    async createFreeSubscription () {
      const { data } = await this.$http.post(API.USERS.createFree, {
        userId: this.id
      })
      if (data && data.err) this.$notify(data.err)
      await this.fetchSubscriptions()
    },
    async cancelFreeSubscription () {
      const { data } = await this.$http.post(API.USERS.cancelFree, {
        userId: this.id
      })
      if (data && data.err) this.$notify(data.err)
      await this.fetchSubscriptions()
    }
  }
}
</script>
